// Line icons for the shell and the dashboard tiles.
//
// Inline SVG rather than an icon package: the app has three runtime
// dependencies and the CSP has no `font-src`, so an icon FONT could not load
// and an icon PACKAGE would be the fourth dependency for ~14 glyphs. They are
// drawn on a 24-grid with a 1.6 stroke and inherit `currentColor`, so a chip,
// a nav row and a tile all get the right colour without a prop.
//
// Every icon here is DECORATIVE: it sits beside a text label that carries the
// meaning, so each one is `aria-hidden`. An icon that ever becomes the only
// label needs a `role="img"` + `aria-label` instead.

import type { CSSProperties } from "react";

export interface IconProps {
  size?: number;
  style?: CSSProperties;
  className?: string;
}

function svg(path: React.ReactNode, { size = 18, style, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={style}
      className={className}
    >
      {path}
    </svg>
  );
}

export const IconDashboard = (p: IconProps) =>
  svg(
    <>
      <rect x="3" y="3" width="7.5" height="8.5" rx="1.6" />
      <rect x="13.5" y="3" width="7.5" height="5.5" rx="1.6" />
      <rect x="3" y="14.5" width="7.5" height="6.5" rx="1.6" />
      <rect x="13.5" y="11.5" width="7.5" height="9.5" rx="1.6" />
    </>,
    p,
  );

export const IconPayables = (p: IconProps) =>
  svg(
    <>
      <rect x="2.5" y="5.5" width="19" height="13" rx="2.2" />
      <path d="M2.5 10h19" />
      <path d="M6.5 14.5h4" />
    </>,
    p,
  );

export const IconTreasury = (p: IconProps) =>
  svg(
    <>
      <path d="M3 9.5 12 4l9 5.5" />
      <path d="M5 9.5V19M19 9.5V19M9.5 9.5V19M14.5 9.5V19" />
      <path d="M3 19.5h18" />
    </>,
    p,
  );

export const IconRefunds = (p: IconProps) =>
  svg(
    <>
      <path d="M20 11a8 8 0 1 0-2.3 5.6" />
      <path d="M20 5v5h-5" />
    </>,
    p,
  );

export const IconApprovals = (p: IconProps) =>
  svg(
    <>
      <path d="M20 7.5v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-11a2 2 0 0 1 2-2h8Z" />
      <path d="M8.5 12.5 11 15l4.5-4.5" />
    </>,
    p,
  );

export const IconHealth = (p: IconProps) =>
  svg(
    <>
      <path d="M3 12.5h4l2.2-5 3.4 10 2.4-5h6" />
    </>,
    p,
  );

export const IconProducts = (p: IconProps) =>
  svg(
    <>
      <path d="M12 3.2 20.2 7.6v8.8L12 20.8 3.8 16.4V7.6Z" />
      <path d="M3.8 7.6 12 12l8.2-4.4M12 12v8.8" />
    </>,
    p,
  );

export const IconSession = (p: IconProps) =>
  svg(
    <>
      <circle cx="12" cy="8.5" r="3.6" />
      <path d="M4.8 20a7.4 7.4 0 0 1 14.4 0" />
    </>,
    p,
  );

export const IconDevices = (p: IconProps) =>
  svg(
    <>
      <rect x="3" y="4.5" width="12.5" height="10" rx="1.8" />
      <path d="M6.5 18.5h6" />
      <rect x="17" y="10" width="4.5" height="9.5" rx="1.4" />
    </>,
    p,
  );

export const IconPartners = (p: IconProps) =>
  svg(
    <>
      <circle cx="9" cy="8.5" r="3.2" />
      <path d="M2.8 19.5a6.4 6.4 0 0 1 12.4 0" />
      <path d="M16.2 5.7a3.2 3.2 0 0 1 0 5.7M17.6 14.3a6.4 6.4 0 0 1 3.6 5.2" />
    </>,
    p,
  );

export const IconCredits = (p: IconProps) =>
  svg(
    <>
      <circle cx="12" cy="12" r="8.6" />
      <path d="M14.6 9.2A3.4 3.4 0 0 0 9.4 10c0 2.7 5.2 1.6 5.2 4.2a3.4 3.4 0 0 1-5.2.8" />
      <path d="M12 6.6v10.8" />
    </>,
    p,
  );

export const IconLegacy = (p: IconProps) =>
  svg(
    <>
      <path d="M4 8.5V5.2A1.2 1.2 0 0 1 5.2 4h13.6A1.2 1.2 0 0 1 20 5.2v3.3" />
      <rect x="3" y="8.5" width="18" height="11.5" rx="1.8" />
      <path d="M9.6 12.5h4.8" />
    </>,
    p,
  );

export const IconWallet = (p: IconProps) =>
  svg(
    <>
      <path d="M3.5 7.4A2 2 0 0 1 5.5 5.4h11.8a1.7 1.7 0 0 1 1.7 1.7v1.3" />
      <rect x="3.5" y="7.4" width="17" height="11.4" rx="2" />
      <circle cx="16.4" cy="13.1" r="1.2" />
    </>,
    p,
  );

export const IconShield = (p: IconProps) =>
  svg(
    <>
      <path d="M12 3.2 19.5 6v6c0 4.4-3.1 7.5-7.5 8.8C7.6 19.5 4.5 16.4 4.5 12V6Z" />
      <path d="M9 12.2 11.2 14.4 15.4 10.2" />
    </>,
    p,
  );

export const IconChevronRight = (p: IconProps) =>
  svg(<path d="M9.5 5.5 16 12l-6.5 6.5" />, p);

export const IconArrowRight = (p: IconProps) =>
  svg(
    <>
      <path d="M4.5 12h14.2" />
      <path d="M13.2 6.2 19 12l-5.8 5.8" />
    </>,
    p,
  );

export const IconAlert = (p: IconProps) =>
  svg(
    <>
      <path d="M12 4.2 21 19.4H3Z" />
      <path d="M12 10v4.2M12 17.1v.1" />
    </>,
    p,
  );

export const IconInfo = (p: IconProps) =>
  svg(
    <>
      <circle cx="12" cy="12" r="8.6" />
      <path d="M12 11v5.4M12 7.9v.1" />
    </>,
    p,
  );

// ── Added for the action-first IA ──────────────────────────────────────
// Section glyphs for the collapsed sidebar, and verb glyphs for the
// Control Plane workflow controls. Same 24-grid, same 1.6 stroke.

export const IconLayers = (p: IconProps) =>
  svg(
    <>
      <path d="M12 3.4 21 8l-9 4.6L3 8Z" />
      <path d="M3.4 12.4 12 16.8l8.6-4.4M3.4 16.4 12 20.8l8.6-4.4" />
    </>,
    p,
  );

export const IconOperations = (p: IconProps) =>
  svg(
    <>
      <path d="M4 6.5h10M18 6.5h2M4 17.5h2M10 17.5h10" />
      <circle cx="16" cy="6.5" r="2.2" />
      <circle cx="8" cy="17.5" r="2.2" />
      <path d="M4 12h16" />
    </>,
    p,
  );

export const IconSystem = (p: IconProps) =>
  svg(
    <>
      <rect x="3" y="3.5" width="18" height="12" rx="2" />
      <path d="M8 19.5h8M12 15.5v4" />
      <path d="M7 9.5h3l1.4-2.6L14 12l1.2-2.5H17" />
    </>,
    p,
  );

export const IconPublish = (p: IconProps) =>
  svg(
    <>
      <path d="M12 16.5V4.2" />
      <path d="M7.4 8.8 12 4.2l4.6 4.6" />
      <path d="M4.5 15v3.3A1.7 1.7 0 0 0 6.2 20h11.6a1.7 1.7 0 0 0 1.7-1.7V15" />
    </>,
    p,
  );

export const IconRollback = (p: IconProps) =>
  svg(
    <>
      <path d="M4 12a8 8 0 1 0 2.4-5.7" />
      <path d="M4 4.5V10h5.5" />
      <path d="M12 8.2V12l2.8 1.8" />
    </>,
    p,
  );

export const IconEdit = (p: IconProps) =>
  svg(
    <>
      <path d="M4 20h4.2L19.4 8.8a2 2 0 0 0 0-2.9l-1.3-1.3a2 2 0 0 0-2.9 0L4 15.8Z" />
      <path d="M14.4 5.9l3.7 3.7" />
    </>,
    p,
  );

export const IconHistory = (p: IconProps) =>
  svg(
    <>
      <circle cx="12" cy="12" r="8.4" />
      <path d="M12 7.2V12l3.4 2" />
    </>,
    p,
  );

export const IconCheck = (p: IconProps) =>
  svg(<path d="M5 12.6 9.6 17 19 7.4" />, p);

export const IconClose = (p: IconProps) =>
  svg(<path d="M6.4 6.4 17.6 17.6M17.6 6.4 6.4 17.6" />, p);

export const IconChevronDown = (p: IconProps) =>
  svg(<path d="M5.5 9 12 15.5 18.5 9" />, p);

export const IconValidate = (p: IconProps) =>
  svg(
    <>
      <path d="M6.5 3.6h11a1.8 1.8 0 0 1 1.8 1.8v13.2a1.8 1.8 0 0 1-1.8 1.8h-11a1.8 1.8 0 0 1-1.8-1.8V5.4a1.8 1.8 0 0 1 1.8-1.8Z" />
      <path d="M8.8 12.2 11 14.4l4.2-4.6" />
    </>,
    p,
  );

export const IconDraft = (p: IconProps) =>
  svg(
    <>
      <path d="M13.6 3.6H7a1.8 1.8 0 0 0-1.8 1.8v13.2A1.8 1.8 0 0 0 7 20.4h10a1.8 1.8 0 0 0 1.8-1.8V8.8Z" />
      <path d="M13.4 3.8v5h5" />
    </>,
    p,
  );
